/**
 * challenge-i18n.ts — i18n challenges (18/06/2026)
 *
 * Helpers uniques pour résoudre les champs de contenu d'un challenge selon la
 * langue active. Évite de répéter la cascade champFr/En/legacy dans chaque
 * composant. Calqué sur offer-i18n.ts (Lot 8).
 *
 * Cascade :
 *   - language === "fr" → champFr || champEn || champ legacy || ""
 *   - autre (en, ...)    → champEn || champFr || champ legacy || ""
 *
 * Les champs legacy (title, description, fictiveCompany..., gift...) sont
 * conservés en dernier recours pour les challenges pas encore migrés / pas
 * encore retraduits par le cron.
 */

type ChallengeI18nFields = {
  title?: string | null;
  description?: string | null;
  fictiveCompanyName?: string | null;
  fictiveCompanyDescription?: string | null;
  giftName?: string | null;
  giftDescription?: string | null;
  titleFr?: string | null;
  titleEn?: string | null;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  fictiveCompanyNameFr?: string | null;
  fictiveCompanyNameEn?: string | null;
  fictiveCompanyDescriptionFr?: string | null;
  fictiveCompanyDescriptionEn?: string | null;
  giftNameFr?: string | null;
  giftNameEn?: string | null;
  giftDescriptionFr?: string | null;
  giftDescriptionEn?: string | null;
};

function isFrench(language: string | undefined): boolean {
  return (language || "fr").toLowerCase().startsWith("fr");
}

function pick(
  fr: string | null | undefined,
  en: string | null | undefined,
  legacy: string | null | undefined,
  language: string | undefined,
): string {
  if (isFrench(language)) {
    return fr || en || legacy || "";
  }
  return en || fr || legacy || "";
}

export function pickChallengeTitle(
  c: ChallengeI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!c) return "";
  return pick(c.titleFr, c.titleEn, c.title, language);
}

export function pickChallengeDescription(
  c: ChallengeI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!c) return "";
  return pick(c.descriptionFr, c.descriptionEn, c.description, language);
}

export function pickChallengeFictiveCompanyName(
  c: ChallengeI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!c) return "";
  return pick(c.fictiveCompanyNameFr, c.fictiveCompanyNameEn, c.fictiveCompanyName, language);
}

export function pickChallengeFictiveCompanyDescription(
  c: ChallengeI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!c) return "";
  return pick(
    c.fictiveCompanyDescriptionFr,
    c.fictiveCompanyDescriptionEn,
    c.fictiveCompanyDescription,
    language,
  );
}

export function pickChallengeGiftName(
  c: ChallengeI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!c) return "";
  return pick(c.giftNameFr, c.giftNameEn, c.giftName, language);
}

export function pickChallengeGiftDescription(
  c: ChallengeI18nFields | null | undefined,
  language: string | undefined,
): string {
  if (!c) return "";
  return pick(c.giftDescriptionFr, c.giftDescriptionEn, c.giftDescription, language);
}
